/**
 * テーマ切り替えの純粋ロジック（docs/design/design-system.md §9）。
 * - 既定は OS 設定。ライト / ダークを選んだら <html data-theme="light|dark">、OS に合わせるなら属性を外す。
 * - 保存先は localStorage の `ic:theme`。Storage と属性を付ける対象は引数で注入する。
 * - `?theme=dark` などの URL クエリは確認用の一時上書き（保存しない）。
 */

export type ThemePreference = 'system' | 'light' | 'dark';

export const THEME_STORAGE_KEY = 'ic:theme';
export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

export type ThemeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
export type ThemeRoot = Pick<Element, 'setAttribute' | 'removeAttribute'>;

export function isThemePreference(v: unknown): v is ThemePreference {
  return typeof v === 'string' && (THEME_PREFERENCES as readonly string[]).includes(v);
}

/** 保存値を読む。無い・壊れている・Storage が使えないときは 'system' */
export function readTheme(storage: ThemeStorage | null | undefined): ThemePreference {
  if (!storage) return 'system';
  try {
    const v = storage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(v) ? v : 'system';
  } catch {
    return 'system';
  }
}

/** 保存する。'system' はキーを消す。書けなかったら false（画面の切り替えは続ける） */
export function writeTheme(
  storage: ThemeStorage | null | undefined,
  pref: ThemePreference,
): boolean {
  if (!storage) return false;
  try {
    if (pref === 'system') storage.removeItem(THEME_STORAGE_KEY);
    else storage.setItem(THEME_STORAGE_KEY, pref);
    return true;
  } catch {
    return false;
  }
}

/** `?theme=dark` / `light` / `os` / `system` を読む。無ければ null */
export function themeFromSearch(search: string): ThemePreference | null {
  const v = new URLSearchParams(search).get('theme');
  if (v === 'os') return 'system';
  return isThemePreference(v) ? v : null;
}

export function applyTheme(root: ThemeRoot, pref: ThemePreference): void {
  if (pref === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', pref);
}

/** 起動時のテーマ。URL の一時上書き → 保存値 → OS の順 */
export function initialTheme(
  storage: ThemeStorage | null | undefined,
  search: string,
): ThemePreference {
  return themeFromSearch(search) ?? readTheme(storage);
}

export const THEME_LABELS: Record<ThemePreference, string> = {
  system: 'OS に合わせる',
  light: 'ライト',
  dark: 'ダーク',
};
